import * as bootstrap from "bootstrap";
import TomSelect from "tom-select";
import { selectDropdownLayerConfig } from "./selectDropdownLayer";
import { escapeHtml, showToast } from "../common";
import { getJson, postJson } from "../api";
import { initDismissibleAlerts } from "./dismissibleAlerts";
import { scrollToGroupHeader } from "./groupTableGroupFocus";
import { loadGroupStudentsList } from "../vue/groupStudentsListApp";
import { createStudentIdentityResolver, getStudentIdentityStorageEvent } from "./studentIdentityStore";
import { isSubjectAllowedForActivityType } from "./assignmentSubjects.js";
import { startSelectLoading, stopSelectLoading } from "./selectLoading";

function buildEditorSelectConfig(placeholder, extra = {}) {
  const { render = {}, plugins = {}, ...rest } = extra;

  return {
    ...selectDropdownLayerConfig,
    create: false,
    allowEmptyOption: true,
    placeholder,
    render: {
      no_results() {
        return '<div class="no-results">Brak wyników</div>';
      },
      loading() {
        return '<div class="loading text-center py-2"><span class="spinner-border spinner-border-sm text-primary" role="status" aria-label="Ładowanie"></span></div>';
      },
      loading_more() {
        return '<div class="loading-more-results text-center py-2"><span class="spinner-border spinner-border-sm text-primary" role="status" aria-label="Ładowanie kolejnych wyników"></span></div>';
      },
      no_more_results() {
        return null;
      },
      ...render,
    },
    plugins: {
      dropdown_input: {},
      ...plugins,
    },
    ...rest,
  };
}

function buildColorOptionMarkup(label, optionClass, variant) {
  const safeLabel = escapeHtml(label);
  if (!optionClass) {
    return `<div>${safeLabel}</div>`;
  }

  return `<div class="ts-color-option ts-color-option--${variant} ${optionClass}"><span class="ts-color-option__label">${safeLabel}</span></div>`;
}

function buildTeacherOptionMarkup(option) {
  const absenceLabel = option?.is_absent
    ? ' <small class="text-body-secondary">(Nieobecność)</small>'
    : "";

  return `<div>${escapeHtml(String(option?.text ?? ""))}${absenceLabel}</div>`;
}

function getSubjectOptionText(option) {
  const explicitText = String(option?.text ?? option?.label ?? option?.subjectDisplayLabel ?? "").trim();
  if (explicitText !== "") {
    return explicitText;
  }

  const subjectLabel = String(option?.subject ?? option?.subjectLabel ?? "").trim();
  const typeName = String(option?.type_name ?? option?.typeName ?? "").trim();

  return [subjectLabel, typeName].filter((part) => part !== "").join(" - ");
}

function getOptionClass(option) {
  const explicitClass = String(option?.optionClass ?? option?.className ?? "").trim();
  if (explicitClass !== "") {
    return explicitClass;
  }

  const typeId = Number.parseInt(String(option?.type_id ?? option?.typeId ?? "0"), 10) || 0;
  return typeId > 0 ? `type-color type-color--${typeId}` : "";
}

function normalizeSubjectOption(option) {
  const value = String(option?.id ?? option?.value ?? "").trim();
  const text = getSubjectOptionText(option);
  const typeId = Number.parseInt(String(option?.type_id ?? option?.typeId ?? "0"), 10) || 0;
  const typeName = String(option?.type_name ?? option?.typeName ?? "").trim();
  const optionClass = getOptionClass(option);

  return {
    id: value,
    value,
    text,
    type_id: typeId,
    type_name: typeName,
    optionClass,
  };
}

function syncCapacityTooltip(capacityEl, nextLabel) {
  if (!(capacityEl instanceof HTMLElement)) {
    return;
  }

  const tooltipLabel = typeof nextLabel === "string" ? nextLabel.trim() : "";
  const tooltip = bootstrap.Tooltip.getInstance(capacityEl);

  if (tooltipLabel !== "") {
    capacityEl.setAttribute("title", tooltipLabel);
    capacityEl.setAttribute("data-bs-original-title", tooltipLabel);

    if (tooltip && typeof tooltip.setContent === "function") {
      tooltip.setContent({ ".tooltip-inner": tooltipLabel });
      return;
    }

    bootstrap.Tooltip.getOrCreateInstance(capacityEl);
    return;
  }

  capacityEl.removeAttribute("title");
  capacityEl.removeAttribute("data-bs-original-title");
  tooltip?.dispose();
}

function getContainer(root) {
  if (root instanceof Element && root.matches(".js-group-assignments")) {
    return root;
  }

  if (root instanceof Document || root instanceof Element) {
    return root.querySelector(".js-group-assignments");
  }

  return null;
}

export function initGroupCreateContextPicker(root = document) {
  if (!(root instanceof Document || root instanceof Element)) {
    return;
  }

  root.querySelectorAll("[data-group-create-context-form]").forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.groupCreateContextPickerInitialized === "1") {
      return;
    }

    form.dataset.groupCreateContextPickerInitialized = "1";
    form.addEventListener("submit", (event) => {
      const select = form.querySelector("[data-group-create-context-select]");
      const targetUrl = select instanceof HTMLSelectElement ? select.value.trim() : "";

      if (targetUrl === "") {
        return;
      }

      event.preventDefault();
      window.location.assign(targetUrl);
    });
  });
}

function isReadonlyPreview(container) {
  return container?.dataset?.groupReadonlyPreview === "1";
}

function getVisibleGroupCheckboxes(container, groupId) {
  return Array.from(container.querySelectorAll(`.js-group-membership[data-group-id="${groupId}"]`))
    .filter((checkbox) => !checkbox.disabled && !checkbox.closest("[data-group-student-row][hidden]"));
}

function initGroupStudentSearch(container) {
  const form = container.querySelector("[data-group-student-search-form]");
  const input = form?.querySelector("[data-group-search]");
  if (!form || !input) {
    return;
  }

  const rows = Array.from(container.querySelectorAll("[data-group-student-row]"));
  const count = container.querySelector("[data-group-student-count]");
  const empty = container.querySelector("[data-group-students-empty]");
  const clear = form.querySelector("[data-group-student-clear]");
  const persistedQuery = container.querySelector("[data-group-student-query]");

  const apply = () => {
    const query = input.value.trim();
    const normalizedQuery = query.toLowerCase();
    const masks = createStudentIdentityResolver().getEntriesForSchool(form.dataset.schoolId);
    let visibleCount = 0;

    rows.forEach((row) => {
      const identifier = row.dataset.groupStudentIdentifier || "";
      row.hidden = ![identifier, row.dataset.groupStudentId || "", masks[identifier] || ""]
        .some((value) => value.toLowerCase().includes(normalizedQuery));
      if (!row.hidden) {
        visibleCount += 1;
      }
    });

    if (count && empty) count.textContent = visibleCount === rows.length ? String(visibleCount) : `${visibleCount} / ${rows.length}`;
    if (empty) empty.hidden = visibleCount > 0;
    if (clear) clear.hidden = query === "";
    if (persistedQuery) persistedQuery.value = query;

    container.querySelectorAll(".js-group-column-toggle").forEach((toggle) => {
      updateColumnToggleState(container, toggle.dataset.groupId || "0");
    });

    const syncQuery = (url) => {
      if (query) url.searchParams.set("q", query);
      else url.searchParams.delete("q");
      ["q_identifiers", "q_mask_label", "q_mask_no_match"].forEach((key) => url.searchParams.delete(key));
      return url;
    };
    container.querySelectorAll(".table-filters a").forEach((link) => {
      link.href = syncQuery(new URL(link.href, window.location.href)).toString();
    });
    window.history.replaceState(window.history.state, "", syncQuery(new URL(window.location.href)).toString());
  };

  input.addEventListener("input", apply);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    apply();
  });
  clear?.addEventListener("click", () => {
    input.value = "";
    apply();
    input.focus();
  });
  window.addEventListener(getStudentIdentityStorageEvent(), apply);
  window.addEventListener("storage", apply);
  apply();
}

function updateColumnToggleState(container, groupId) {
  const toggle = container.querySelector(`.js-group-column-toggle[data-group-id="${groupId}"]`);
  if (!toggle) {
    return;
  }

  const enabledCheckboxes = getVisibleGroupCheckboxes(container, groupId);

  if (enabledCheckboxes.length === 0) {
    toggle.checked = false;
    toggle.indeterminate = false;
    return;
  }

  const checkedCount = enabledCheckboxes.filter((checkbox) => checkbox.checked).length;
  toggle.checked = checkedCount === enabledCheckboxes.length;
  toggle.indeterminate = checkedCount > 0 && checkedCount < enabledCheckboxes.length;
}

function getEditorForm(container) {
  const form = container.querySelector(".js-group-create-form");
  return form instanceof HTMLFormElement ? form : null;
}

function getEditorModalElement(container) {
  const modal = container.querySelector(".js-group-create-modal");
  return modal instanceof HTMLElement ? modal : null;
}

function getEditorModal(container) {
  const modalEl = getEditorModalElement(container);
  return modalEl ? bootstrap.Modal.getOrCreateInstance(modalEl) : null;
}

function getSplitForm(container) {
  const form = container.querySelector(".js-group-split-form");
  return form instanceof HTMLFormElement ? form : null;
}

function getSplitModalElement(container) {
  const modal = container.querySelector(".js-group-split-modal");
  return modal instanceof HTMLElement ? modal : null;
}

function getSplitModal(container) {
  const modalEl = getSplitModalElement(container);
  return modalEl ? bootstrap.Modal.getOrCreateInstance(modalEl) : null;
}

function getStudentsForm(container) {
  const form = container.querySelector(".js-group-students-form");
  return form instanceof HTMLFormElement ? form : null;
}

function getStudentsModalElement(container) {
  const modal = container.querySelector(".js-group-students-modal");
  return modal instanceof HTMLElement ? modal : null;
}

function getStudentsModal(container) {
  const modalEl = getStudentsModalElement(container);
  return modalEl ? bootstrap.Modal.getOrCreateInstance(modalEl) : null;
}

function getTeacherField(container) {
  const field = container.querySelector('select[name="teacher_id"]');
  return field instanceof HTMLSelectElement ? field : null;
}

function getSubjectField(container) {
  const field = container.querySelector('select[name="subject_id"]');
  return field instanceof HTMLSelectElement ? field : null;
}

function getSubject2Field(container) {
  const field = container.querySelector('select[name="subject_id_2"]');
  return field instanceof HTMLSelectElement ? field : null;
}

function getActivityTypeField(container) {
  const field = container.querySelector('select[name="activity_type_id"]');
  return field instanceof HTMLSelectElement ? field : null;
}

function getTeacherSelect(container) {
  return getTeacherField(container)?.tomselect ?? null;
}

function getSubjectSelect(container) {
  return getSubjectField(container)?.tomselect ?? null;
}

function getSubject2Select(container) {
  return getSubject2Field(container)?.tomselect ?? null;
}

function getActivityTypeSelect(container) {
  return getActivityTypeField(container)?.tomselect ?? null;
}

function ensureTomSelectOption(instance, option) {
  const normalizedOption = normalizeSubjectOption(option);
  const value = normalizedOption.value;
  const text = normalizedOption.text;

  if (!instance || value === "" || text === "") {
    return;
  }

  const optionPayload = {
    id: value,
    value,
    text,
    optionClass: normalizedOption.optionClass,
    type_id: normalizedOption.type_id,
    type_name: normalizedOption.type_name,
  };

  if (Object.prototype.hasOwnProperty.call(option ?? {}, "is_absent")) {
    optionPayload.is_absent = Boolean(option.is_absent);
  }

  if (instance.options[value]) {
    const currentOption = instance.options[value];
    const nextOption = {
      ...currentOption,
      ...optionPayload,
    };

    if (typeof instance.updateOption === "function") {
      instance.updateOption(value, nextOption);
      return;
    }
  }

  if (!instance.options[value]) {
    instance.addOption(optionPayload);
  }
}

function setTeacherPlaceholder(container, label = "Najpierw wybierz przedmiot") {
  const teacherField = getTeacherField(container);
  const teacherSelect = getTeacherSelect(container);
  if (!teacherField || !teacherSelect) {
    return;
  }

  teacherSelect.clear(true);
  teacherSelect.clearOptions();
  teacherSelect.addOption({ value: "", text: label });
  teacherSelect.refreshOptions(false);
  teacherField.dataset.placeholder = label;
  teacherSelect.settings.placeholder = label;
}

function disableTeacherField(container, label = "Najpierw wybierz przedmiot") {
  const teacherField = getTeacherField(container);
  const teacherSelect = getTeacherSelect(container);
  if (!teacherField || !teacherSelect) {
    return;
  }

  stopSelectLoading(teacherField);
  setTeacherPlaceholder(container, label);
  teacherField.disabled = true;
  teacherSelect.disable();
}

function enableTeacherField(container, emptyLabel = "Wybierz nauczyciela") {
  const teacherField = getTeacherField(container);
  const teacherSelect = getTeacherSelect(container);
  if (!teacherField || !teacherSelect) {
    return;
  }

  teacherField.disabled = false;
  teacherSelect.enable();
  teacherField.dataset.placeholder = emptyLabel;
  teacherSelect.settings.placeholder = emptyLabel;
}

async function fetchSelectOptions(url, query = "", limit = 20, page = 1) {
  if (!url) {
    return { results: [], pagination: {} };
  }

  const requestUrl = new URL(url, window.location.origin);
  requestUrl.searchParams.set("q", query);
  requestUrl.searchParams.set("limit", String(limit));
  requestUrl.searchParams.set("page", String(page));

  const payload = await getJson(requestUrl.toString());
  return {
    results: Array.isArray(payload?.results) ? payload.results : [],
    pagination: payload?.pagination ?? {},
  };
}

function initEditorSelects(container) {
  const subjectField = getSubjectField(container);
  const subject2Field = getSubject2Field(container);
  const teacherField = getTeacherField(container);
  const subjectsApiUrl = container.dataset.groupSubjectsApi || "";
  const requestLimit = 20;

  const buildSubjectsRequestUrl = (query, page = 1, activityTypeId = "") => {
    const requestUrl = new URL(subjectsApiUrl, window.location.origin);
    requestUrl.searchParams.set("q", query);
    requestUrl.searchParams.set("limit", String(requestLimit));
    requestUrl.searchParams.set("page", String(page));
    requestUrl.searchParams.set("activity_type_id", String(activityTypeId));
    return requestUrl;
  };

  const hasMoreSubjectPages = (pagination, page) => {
    const pages = Number(pagination?.pages);
    if (Number.isFinite(pages) && pages > 0) {
      return page < pages;
    }

    const total = Number(pagination?.total);
    return Number.isFinite(total) && total >= 0 && page * requestLimit < total;
  };

  const buildSubjectSelect = (field, placeholder, extraConfig = {}) => {
    if (!field || field.tomselect) {
      return;
    }

    const activityField = getEditorForm(container)?.querySelector(
      `select[name="${field.name === "subject_id_2" ? "activity_type_id_2" : "activity_type_id"}"]`,
    );
    const rules = JSON.parse(activityField?.dataset.subjectRules || "{}");
    const buildRequestUrl = (query, page = 1) => buildSubjectsRequestUrl(query, page, activityField?.value || "");

    new TomSelect(
      field,
      buildEditorSelectConfig(placeholder, {
        valueField: "id",
        labelField: "text",
        searchField: ["text", "type_name"],
        preload: true,
        loadThrottle: 300,
        maxOptions: null,
        plugins: { virtual_scroll: {} },
        firstUrl(query) {
          return buildRequestUrl(query).toString();
        },
        render: {
          option(data) {
            return buildColorOptionMarkup(String(data?.text ?? ""), String(data?.optionClass ?? "").trim(), "dropdown");
          },
          item(data) {
            return buildColorOptionMarkup(String(data?.text ?? ""), String(data?.optionClass ?? "").trim(), "item");
          },
        },
        shouldLoad(query) {
          return query.length === 0 || query.trim().length >= 2;
        },
        async load(query, callback) {
          const finishLoading = startSelectLoading(field);
          const activityTypeId = activityField?.value || "";
          let requestUrl = buildRequestUrl(query);
          if (typeof this?.getUrl === "function") {
            const nextUrl = this.getUrl(query);
            if (nextUrl) {
              requestUrl = new URL(nextUrl, window.location.origin);
            }
          }

          const page = Math.max(Number.parseInt(requestUrl.searchParams.get("page") || "1", 10) || 1, 1);

          try {
            const payload = await fetchSelectOptions(requestUrl.toString(), query, requestLimit, page);
            if (activityTypeId !== (activityField?.value || "") || query !== this.inputValue()) {
              callback(Object.values(this.options));
              return;
            }
            if (typeof this?.setNextUrl === "function") {
              this.setNextUrl(
                query,
                hasMoreSubjectPages(payload.pagination, page)
                  ? buildRequestUrl(query, page + 1).toString()
                  : false,
              );
            }

            callback(
              payload.results.map(normalizeSubjectOption).filter((option) => option.id !== "" && option.text !== ""),
            );
          } catch {
            if (activityTypeId !== (activityField?.value || "") || query !== this.inputValue()) {
              callback(Object.values(this.options));
              return;
            }
            if (typeof this?.setNextUrl === "function") {
              this.setNextUrl(query, false);
            }

            callback([]);
          } finally {
            finishLoading();
          }
        },
        ...extraConfig,
      }),
    );

    const validate = () => {
      const selected = field.tomselect.options[field.value];
      const valid = !field.value || isSubjectAllowedForActivityType(selected?.type_id, activityField?.value, rules);
      field.setCustomValidity(valid ? "" : "Zmień przedmiot lub charakter zajęć. Ten przedmiot nie jest dozwolony dla wybranego charakteru zajęć.");
      field.classList.toggle("is-invalid", !valid);
      return valid;
    };
    field._refreshActivitySubjects = (clearInvalid = false) => {
      const instance = field.tomselect;
      if (!validate() && clearInvalid) instance.clear();
      validate();
      instance.clearOptions();
      instance.loadedSearches = {};
      instance.clearPagination?.();
      startSelectLoading(field, { lock: true });
      instance.load(instance.inputValue());
    };
    field.addEventListener("change", validate);
    activityField?.addEventListener("change", () => field._refreshActivitySubjects(true));
  };

  buildSubjectSelect(subjectField, "Zacznij wpisywać nazwę przedmiotu...", {
    onChange(value) {
      if (container.dataset.groupEditorSync === "1") {
        return;
      }

      const subjectId = Number.parseInt(String(value || "0"), 10) || 0;
      void loadTeachersForSubject(container, subjectId, { silent: false });
    },
  });
  buildSubjectSelect(subject2Field, "Wybierz drugi przedmiot");

  if (teacherField && !teacherField.tomselect) {
    new TomSelect(
      teacherField,
      buildEditorSelectConfig("Najpierw wybierz przedmiot", {
        render: {
          option: buildTeacherOptionMarkup,
          item: buildTeacherOptionMarkup,
        },
      }),
    );
  }

  disableTeacherField(container);
}

function withEditorSync(container, callback) {
  container.dataset.groupEditorSync = "1";

  try {
    callback();
  } finally {
    delete container.dataset.groupEditorSync;
  }
}

function resetEditorSelects(container) {
  initEditorSelects(container);

  const subjectSelect = getSubjectSelect(container);
  const subject2Select = getSubject2Select(container);
  const activityTypeField = getActivityTypeField(container);
  const activityTypeSelect = getActivityTypeSelect(container);

  if (subjectSelect) {
    withEditorSync(container, () => {
      subjectSelect.clear(true);
    });
  }

  if (subject2Select) {
    withEditorSync(container, () => {
      subject2Select.clear(true);
    });
  }

  if (activityTypeField && activityTypeSelect) {
    withEditorSync(container, () => {
      activityTypeSelect.setValue(String(activityTypeField.value || ""), true);
    });
  }

  disableTeacherField(container);
}

async function loadTeachersForSubject(container, subjectId, options = {}) {
  const { selectedTeacherId = "", selectedTeacherLabel = "", silent = false } = options;
  const teacherField = getTeacherField(container);
  const teacherSelect = getTeacherSelect(container);
  const template = container.dataset.groupSubjectTeachersApiTemplate || "";

  if (!teacherField || !teacherSelect) {
    return [];
  }

  if (!subjectId || !template) {
    disableTeacherField(container);
    return [];
  }

  disableTeacherField(container, "Ladowanie nauczycieli...");
  const finishLoading = startSelectLoading(teacherField);

  try {
    const { results } = await fetchSelectOptions(template.replace("__SUBJECT_ID__", String(subjectId)), "", 100);
    if (subjectId !== Number(getSubjectField(container)?.value || 0)) return [];

    teacherSelect.clear(true);
    teacherSelect.clearOptions();
    teacherSelect.addOption({ value: "", text: "Wybierz nauczyciela" });

    results.forEach((result) => {
      ensureTomSelectOption(teacherSelect, result);
    });

    if (selectedTeacherId && selectedTeacherLabel) {
      ensureTomSelectOption(teacherSelect, {
        id: String(selectedTeacherId),
        text: String(selectedTeacherLabel),
      });
    }

    teacherSelect.refreshOptions(false);
    enableTeacherField(container);

    withEditorSync(container, () => {
      teacherSelect.setValue(String(selectedTeacherId || ""), true);
    });

    return results;
  } catch (error) {
    if (subjectId !== Number(getSubjectField(container)?.value || 0)) return [];
    disableTeacherField(container, "Nie udalo sie wczytac nauczycieli");

    if (!silent) {
      showToast(String(error?.message || "Nie udało się pobrać listy nauczycieli."), { type: "danger" });
    }

    return [];
  } finally {
    finishLoading();
  }
}

async function setEditorMode(container, mode, group = null) {
  const form = getEditorForm(container);
  const title = container.querySelector(".js-group-editor-title");
  const submitButton = container.querySelector(".js-group-create-submit");

  if (!form) {
    return;
  }

  form.reset();
  initEditorSelects(container);
  resetEditorSelects(container);

  const groupIdField = form.querySelector('input[name="group_id"]');
  const nameField = form.querySelector('input[name="name"]');
  const subjectSelect = getSubjectSelect(container);
  const subject2Select = getSubject2Select(container);
  const teacherSelect = getTeacherSelect(container);
  const activityTypeField = form.querySelector('select[name="activity_type_id"]');
  const activityTypeSelect = getActivityTypeSelect(container);
  const activityType2Field = form.querySelector('select[name="activity_type_id_2"]');
  const hoursField = form.querySelector('input[name="hours"]');

  if (groupIdField instanceof HTMLInputElement) {
    groupIdField.value = mode === "edit" ? String(group?.id || "") : "";
  }

  if (mode === "edit") {
    if (title) {
      title.textContent = "Edycja grupy";
    }
    if (submitButton) {
      submitButton.textContent = "Zapisz zmiany";
    }
    if (nameField instanceof HTMLInputElement) {
      nameField.value = String(group?.name || "");
    }
    if (subjectSelect && group?.subjectId) {
      ensureTomSelectOption(subjectSelect, {
        id: String(group.subjectId || ""),
        text: String(group.subjectDisplayLabel || group.subjectLabel || ""),
        typeId: String(group.subjectTypeId || ""),
        typeName: String(group.subjectTypeName || ""),
        optionClass: String(group.subjectOptionClass || ""),
      });
      withEditorSync(container, () => {
        subjectSelect.setValue(String(group.subjectId || ""), true);
      });
    }
    if (teacherSelect && group?.subjectId) {
      await loadTeachersForSubject(container, Number.parseInt(String(group.subjectId || "0"), 10) || 0, {
        selectedTeacherId: String(group?.teacherId || ""),
        selectedTeacherLabel: String(group?.teacherLabel || ""),
      });
    }
    if (subject2Select && group?.subjectId2) {
      ensureTomSelectOption(subject2Select, {
        id: String(group.subjectId2 || ""),
        text: String(group.subjectDisplayLabel2 || group.subjectLabel2 || ""),
        typeId: String(group.subjectTypeId2 || ""),
        typeName: String(group.subjectTypeName2 || ""),
        optionClass: String(group.subjectOptionClass2 || ""),
      });
      withEditorSync(container, () => {
        subject2Select.setValue(String(group.subjectId2 || ""), true);
      });
    }
    if (teacherSelect && group?.teacherId) {
      ensureTomSelectOption(teacherSelect, {
        id: String(group.teacherId || ""),
        text: String(group.teacherLabel || ""),
      });
    }
    if (activityTypeField instanceof HTMLSelectElement) {
      const nextActivityTypeValue = String(group?.activityTypeId || "");
      activityTypeField.value = nextActivityTypeValue;

      if (activityTypeSelect) {
        withEditorSync(container, () => {
          activityTypeSelect.setValue(nextActivityTypeValue, true);
        });
      }
    }
    if (activityType2Field instanceof HTMLSelectElement) {
      activityType2Field.value = String(group?.activityTypeId2 || "");
    }
    if (hoursField instanceof HTMLInputElement) {
      hoursField.value = String(group?.hours || "");
    }
    container.dataset.groupEditorMode = "edit";
    refreshActivitySubjectOptions(container);
    return;
  }

  if (title) {
    title.textContent = "Nowa grupa";
  }
  if (submitButton) {
    submitButton.textContent = "Zapisz grupę";
  }
  container.dataset.groupEditorMode = "create";
  refreshActivitySubjectOptions(container);
}

function refreshActivitySubjectOptions(container) {
  getSubjectField(container)?._refreshActivitySubjects?.();
  getSubject2Field(container)?._refreshActivitySubjects?.();
}

function getEditorGroupFromButton(button) {
  return {
    id: button.dataset.groupId || "",
    name: button.dataset.groupName || "",
    teacherId: button.dataset.groupTeacherId || "",
    teacherLabel: button.dataset.groupTeacherLabel || "",
    subjectId: button.dataset.groupSubjectId || "",
    subjectLabel: button.dataset.groupSubjectLabel || "",
    subjectDisplayLabel: button.dataset.groupSubjectDisplayLabel || "",
    subjectTypeId: button.dataset.groupSubjectTypeId || "",
    subjectTypeName: button.dataset.groupSubjectTypeName || "",
    subjectOptionClass: button.dataset.groupSubjectOptionClass || "",
    activityTypeId: button.dataset.groupActivityTypeId || "",
    subjectId2: button.dataset.groupSubject2Id || "",
    subjectLabel2: button.dataset.groupSubject2Label || "",
    subjectDisplayLabel2: button.dataset.groupSubject2DisplayLabel || "",
    subjectTypeId2: button.dataset.groupSubjectTypeId2 || "",
    subjectTypeName2: button.dataset.groupSubjectTypeName2 || "",
    subjectOptionClass2: button.dataset.groupSubjectOptionClass2 || "",
    activityTypeId2: button.dataset.groupActivityType2Id || "",
    hours: button.dataset.groupHours || "",
  };
}

function getRequestedRpnFocusFields() {
  const value = new URL(window.location.href).searchParams.get("rpn_focus") || "";
  return new Set(
    value
      .split(",")
      .map((field) => field.trim())
      .filter(Boolean),
  );
}

function highlightRequestedRpnFields(container) {
  const form = getEditorForm(container);
  if (!form) {
    return;
  }

  form.querySelectorAll(".rpn-field-highlight").forEach((element) => element.classList.remove("rpn-field-highlight"));
  const focusFields = getRequestedRpnFocusFields();
  const selectors = [];

  if (focusFields.has("activity_type")) {
    selectors.push('select[name="activity_type_id"]');
  }
  if (focusFields.has("hours")) {
    selectors.push('input[name="hours"]');
  }

  selectors.forEach((selector) => {
    const field = form.querySelector(selector);
    if (!(field instanceof HTMLElement)) {
      return;
    }

    const wrapper = field.closest(".form__row") || field;
    wrapper.classList.add("rpn-field-highlight");
  });
}

async function openRequestedEditor(container) {
  const action = String(container.dataset.groupEditorAutoAction || "").trim();
  if (action === "create") {
    await setEditorMode(container, "create");
    setCreatePanelState(container, true);
    return;
  }

  if (action !== "edit") {
    return;
  }

  const groupId = String(container.dataset.groupEditorAutoGroupId || "").trim();
  const button = Array.from(container.querySelectorAll(".js-group-edit")).find(
    (candidate) => candidate instanceof HTMLButtonElement && candidate.dataset.groupId === groupId,
  );
  if (button instanceof HTMLButtonElement) {
    await setEditorMode(container, "edit", getEditorGroupFromButton(button));
    highlightRequestedRpnFields(container);
    setCreatePanelState(container, true);
  }
}

function reloadWithoutRequestedEditorAction() {
  const url = new URL(window.location.href);
  url.searchParams.delete("group_action");
  url.searchParams.delete("group_id");
  window.location.assign(url.toString());
}

function setCreatePanelState(container, isOpen) {
  const modal = getEditorModal(container);

  if (isOpen) {
    modal?.show();
  } else {
    modal?.hide();
  }
}

function setSplitPanelState(container, isOpen) {
  const modal = getSplitModal(container);

  if (isOpen) {
    modal?.show();
  } else {
    modal?.hide();
  }
}

function setStudentsPanelState(container, isOpen) {
  const modal = getStudentsModal(container);

  if (isOpen) {
    modal?.show();
  } else {
    modal?.hide();
  }
}

function setCreateSubmitting(form, submitting) {
  form.querySelectorAll("input, select, button").forEach((field) => {
    field.disabled = submitting;
  });
}

function setSplitSubmitting(form, submitting) {
  form.querySelectorAll("input, button").forEach((field) => {
    field.disabled = submitting;
  });
}

function setStudentsSubmitting(form, submitting) {
  form.querySelectorAll("select, button").forEach((field) => {
    if (submitting) {
      if (field.disabled) {
        field.dataset.wasDisabled = "1";
      }
      field.disabled = true;
      return;
    }

    field.disabled = field.dataset.wasDisabled === "1" || field.getAttribute("aria-disabled") === "true";
    delete field.dataset.wasDisabled;
  });
}

function setSplitMode(container, group = null) {
  const form = getSplitForm(container);
  if (!form) {
    return;
  }

  form.reset();

  const groupIdField = form.querySelector('input[name="group_id"]');
  const splitCountField = form.querySelector('input[name="split_count"]');
  const description = container.querySelector(".js-group-split-description");
  const groupName = String(group?.name || "").trim();

  if (groupIdField instanceof HTMLInputElement) {
    groupIdField.value = String(group?.id || "");
  }

  if (splitCountField instanceof HTMLInputElement) {
    splitCountField.value = "2";
  }

  if (description) {
    description.textContent =
      groupName !== ""
        ? `Wskaż, ile grup ma istnieć po podziale grupy \"${groupName}\".`
        : "Wskaż, ile grup ma istnieć po podziale wybranej pozycji.";
  }
}

function rebuildStudentsSourceOptions(container, targetGroupId) {
  const form = getStudentsForm(container);
  const select = form?.querySelector('select[name="source_group_id"]');
  const submitButton = form?.querySelector(".js-group-students-submit");

  if (!(select instanceof HTMLSelectElement)) {
    return;
  }

  if (!Array.isArray(form._sourceGroupOptions)) {
    form._sourceGroupOptions = Array.from(select.options).map((option) => ({
      value: option.value,
      text: option.textContent || "",
    }));
  }

  const availableOptions = form._sourceGroupOptions.filter((option) => {
    if (option.value === "") {
      return false;
    }

    return (Number.parseInt(option.value, 10) || 0) !== targetGroupId;
  });

  select.innerHTML = "";

  const placeholderOption = document.createElement("option");
  placeholderOption.value = "";
  placeholderOption.textContent = availableOptions.length > 0 ? "wybierz..." : "Brak innych grup w tym widoku";
  select.appendChild(placeholderOption);

  availableOptions.forEach((option) => {
    const optionEl = document.createElement("option");
    optionEl.value = option.value;
    optionEl.textContent = option.text;
    select.appendChild(optionEl);
  });

  select.disabled = availableOptions.length === 0;

  if (submitButton instanceof HTMLButtonElement && !submitButton.hasAttribute("aria-disabled")) {
    submitButton.disabled = availableOptions.length === 0;
  }
}

function setStudentsMode(container, group = null) {
  const form = getStudentsForm(container);
  if (!form) {
    return;
  }

  form.reset();

  const groupId = Number.parseInt(String(group?.id || "0"), 10) || 0;
  const groupIdField = form.querySelector('input[name="group_id"]');
  const nameField = form.querySelector('input[name="group_name"]');
  const teacherField = form.querySelector('input[name="group_teacher"]');

  if (groupIdField instanceof HTMLInputElement) {
    groupIdField.value = groupId > 0 ? String(groupId) : "";
  }

  if (nameField instanceof HTMLInputElement) {
    nameField.value = String(group?.name || "");
  }

  if (teacherField instanceof HTMLInputElement) {
    teacherField.value = String(group?.teacherLabel || "");
  }

  rebuildStudentsSourceOptions(container, groupId);
  void loadGroupStudentsList(container, groupId);
}

async function sendGroupCreate(container, formData) {
  const apiUrl = container.dataset.groupCreateApi;
  const csrfToken = container.dataset.groupAssignmentsCsrf || "";
  const schoolClassId = Number.parseInt(container.dataset.groupAssignmentsSchoolClassId || "0", 10) || 0;
  const groupId = Number.parseInt(String(formData.get("group_id") || "0"), 10) || 0;
  const teacherId = Number.parseInt(String(formData.get("teacher_id") || "0"), 10) || 0;
  const subjectId = Number.parseInt(String(formData.get("subject_id") || "0"), 10) || 0;

  return postJson(
    apiUrl,
    {
      group_id: groupId,
      school_class_id: schoolClassId,
      name: String(formData.get("name") || "").trim(),
      teacher_id: teacherId,
      subject_id: subjectId,
      subject_teacher: teacherId > 0 && subjectId > 0 ? `${subjectId}_${teacherId}` : "",
      activity_type_id: Number.parseInt(String(formData.get("activity_type_id") || "0"), 10) || 0,
      subject_id_2: Number.parseInt(String(formData.get("subject_id_2") || "0"), 10) || 0,
      activity_type_id_2: Number.parseInt(String(formData.get("activity_type_id_2") || "0"), 10) || 0,
      hours: String(formData.get("hours") || "").trim(),
    },
    csrfToken,
  );
}

async function handleCreateFormSubmit(container, form) {
  if (isReadonlyPreview(container)) {
    showToast("Podglad administratora pozwala przegladac formularz, ale zapis pozostaje zablokowany.", {
      type: "info",
    });
    return;
  }

  const groupIdField = form.querySelector('input[name="group_id"]');
  const groupId = groupIdField instanceof HTMLInputElement ? Number.parseInt(groupIdField.value || "0", 10) || 0 : 0;
  const formData = new FormData(form);
  setCreateSubmitting(form, true);

  try {
    const payload = await sendGroupCreate(container, formData);
    showToast(payload?.toast?.message || (groupId > 0 ? "Zapisano grupę." : "Dodano grupę."), {
      type: payload?.toast?.type || "success",
    });
    if (payload?.toast?.type === "warning") {
      // Preserve the warning through the group matrix reload.
      window.sessionStorage.setItem(`group-workload-warning:${window.location.pathname}`, payload.toast.message);
    }
    reloadWithoutRequestedEditorAction();
  } catch (error) {
    showToast(String(error?.message || (groupId > 0 ? "Nie udało się zapisać grupy." : "Nie udało się dodać grupy.")), {
      type: "danger",
    });
    setCreateSubmitting(form, false);
  }
}

async function sendGroupSplit(container, formData) {
  const template = container.dataset.groupSplitApiTemplate || "";
  const csrfToken = container.dataset.groupAssignmentsCsrf || "";
  const groupId = Number.parseInt(String(formData.get("group_id") || "0"), 10) || 0;

  if (groupId <= 0 || template === "") {
    throw new Error("Nie udało się ustalić grupy do podziału.");
  }

  return postJson(
    template.replace("__GROUP_ID__", String(groupId)),
    {
      split_count: Number.parseInt(String(formData.get("split_count") || "0"), 10) || 0,
    },
    csrfToken,
  );
}

async function handleSplitFormSubmit(container, form) {
  const formData = new FormData(form);
  setSplitSubmitting(form, true);

  try {
    const payload = await sendGroupSplit(container, formData);
    showToast(payload?.toast?.message || "Podzielono grupę.", {
      type: payload?.toast?.type || "success",
    });
    window.location.reload();
  } catch (error) {
    showToast(String(error?.message || "Nie udało się podzielić grupy."), {
      type: "danger",
    });
    setSplitSubmitting(form, false);
  }
}

async function sendGroupStudentsCopy(container, formData) {
  const template = container.dataset.groupStudentsCopyApiTemplate || "";
  const csrfToken = container.dataset.groupAssignmentsCsrf || "";
  const groupId = Number.parseInt(String(formData.get("group_id") || "0"), 10) || 0;

  if (groupId <= 0 || template === "") {
    throw new Error("Nie udało się ustalić grupy docelowej.");
  }

  return postJson(
    template.replace("__GROUP_ID__", String(groupId)),
    {
      source_group_id: Number.parseInt(String(formData.get("source_group_id") || "0"), 10) || 0,
    },
    csrfToken,
  );
}

async function handleStudentsFormSubmit(container, form) {
  if (isReadonlyPreview(container)) {
    showToast("Podglad administratora pozwala przegladac formularz, ale zapis pozostaje zablokowany.", {
      type: "info",
    });
    return;
  }

  const formData = new FormData(form);
  setStudentsSubmitting(form, true);

  try {
    const payload = await sendGroupStudentsCopy(container, formData);
    showToast(payload?.toast?.message || "Skopiowano uczniów do grupy.", {
      type: payload?.toast?.type || "success",
    });
    window.location.reload();
  } catch (error) {
    showToast(String(error?.message || "Nie udało się skopiować uczniów do grupy."), {
      type: "danger",
    });
    setStudentsSubmitting(form, false);

    const groupIdField = form.querySelector('input[name="group_id"]');
    const groupId = groupIdField instanceof HTMLInputElement ? Number.parseInt(groupIdField.value || "0", 10) || 0 : 0;
    rebuildStudentsSourceOptions(container, groupId);
  }
}

async function deleteInactiveGroups(container) {
  const modal = container.querySelector(".js-group-delete-inactive-modal");
  const errorElement = modal?.querySelector("[data-group-delete-inactive-error]");
  const url = container.dataset.groupDeleteInactiveApi || "";
  const groupIds = Array.from(container.querySelectorAll("[data-group-missing-teacher-alerts] [data-group-alert-item]"))
    .map((item) => Number(item.dataset.groupAlertItem));
  if (!modal || !errorElement || !url || groupIds.length === 0) return;

  const buttons = modal.querySelectorAll("button");
  if (modal.querySelector(".js-group-delete-inactive-confirm")?.disabled) return;
  buttons.forEach((button) => { button.disabled = true; });
  errorElement.hidden = true;

  try {
    const payload = await postJson(url, { group_ids: groupIds }, container.dataset.groupAssignmentsCsrf || "");
    showToast(payload?.message || "Usunięto grupy i wypisano uczniów.", { type: "success" });
    window.location.reload();
  } catch (error) {
    errorElement.textContent = String(error?.message || "Nie udało się usunąć grup.");
    errorElement.hidden = false;
    buttons.forEach((button) => { button.disabled = false; });
  }
}

async function deleteGroup(container, button) {
  const groupId = Number.parseInt(button.dataset.groupId || "0", 10) || 0;
  const groupName = String(button.dataset.groupName || "").trim();
  const template = container.dataset.groupDeleteApiTemplate || "";
  const csrfToken = container.dataset.groupAssignmentsCsrf || "";

  if (groupId <= 0 || template === "") {
    showToast("Nie udało się ustalić grupy do usunięcia.", { type: "danger" });
    return;
  }

  const label = groupName !== "" ? `"${groupName}"` : "tę grupę";
  if (!window.confirm(`Usunąć ${label}?`)) {
    return;
  }

  button.disabled = true;

  try {
    const payload = await postJson(template.replace("__GROUP_ID__", String(groupId)), {}, csrfToken);
    showToast(payload?.toast?.message || "Usunięto grupę.", { type: payload?.toast?.type || "success" });
    window.location.reload();
  } catch (error) {
    button.disabled = false;
    showToast(String(error?.message || "Nie udało się usunąć grupy."), { type: "danger" });
  }
}

function renderGroupCount(container, groupId, countPayload) {
  if (!countPayload) {
    return;
  }

  const hasCapacityWarning = Boolean(
    countPayload.has_capacity_warning ?? countPayload.is_under_capacity ?? countPayload.is_over_capacity,
  );

  const countEl = container.querySelector(`[data-group-count-id="${groupId}"]`);
  if (countEl) {
    countEl.textContent = String(countPayload.students_count ?? 0);
  }

  const capacityEl = container.querySelector(`[data-group-capacity-id="${groupId}"]`);
  if (capacityEl) {
    capacityEl.classList.toggle("text-warning", hasCapacityWarning);
    capacityEl.classList.toggle("fw-semibold", hasCapacityWarning);
    capacityEl.dataset.groupCapacityWarning = hasCapacityWarning ? "1" : "0";

    syncCapacityTooltip(capacityEl, countPayload.capacity_tooltip_label);
  }

  const maxEl = container.querySelector(`[data-group-max-id="${groupId}"]`);
  if (maxEl && countPayload.max_students !== undefined) {
    maxEl.textContent = String(countPayload.max_students ?? 0);
  }

  const headerEl = container.querySelector(`[data-group-header-id="${groupId}"]`);

  refreshGroupAlerts(container);
  updateColumnToggleState(container, groupId);
}

function refreshGroupAlerts(container) {
  const alertsElement = container.querySelector("[data-group-alerts]");
  if (!alertsElement) {
    return;
  }

  const list = alertsElement.querySelector("ul");
  if (!list) {
    return;
  }

  const items = Array.from(container.querySelectorAll("[data-group-header-id]"))
    .map((header) => {
      const groupId = String(header.dataset.groupHeaderId || "").trim();
      const capacity = header.querySelector("[data-group-capacity-id]");
      const count = header.querySelector("[data-group-count-id]");
      const max = header.querySelector("[data-group-max-id]");
      if (!groupId || !capacity || capacity.dataset.groupCapacityWarning !== "1") {
        return null;
      }

      const label = String(header.dataset.groupAlertLabel || `Grupa ${groupId}`).trim();
      const countLabel = String(count?.textContent || "0").trim();
      const maxLabel = String(max?.textContent || "").trim();
      const message =
        maxLabel !== ""
          ? `${label}: liczba uczniów ${countLabel}/${maxLabel} poza limitami.`
          : `${label}: liczba uczniów poza limitami.`;
      return `<li data-group-alert-item="${escapeHtml(groupId)}"><a href="#group-${encodeURIComponent(groupId)}">${escapeHtml(message)}</a></li>`;
    })
    .filter((item) => item !== null);

  list.innerHTML = items.join("");
  const countElement = alertsElement.querySelector("[data-group-alert-count]");
  if (countElement) {
    countElement.textContent = String(items.length);
  }
  alertsElement.classList.toggle("d-none", items.length === 0);
  initDismissibleAlerts(alertsElement);
}

function setGroupAlertColumnHighlight(container, groupId, isHighlighted) {
  const normalizedGroupId = String(groupId || "").trim();
  if (normalizedGroupId === "") {
    return;
  }

  container
    .querySelectorAll(`[data-group-header-id="${normalizedGroupId}"], [data-group-column-id="${normalizedGroupId}"]`)
    .forEach((element) => element.classList.toggle("is-alert-highlighted", isHighlighted));
}

function scrollToGroupAlertColumn(container, groupId) {
  const header = container.querySelector(`[data-group-header-id="${String(groupId || "").trim()}"]`);
  if (header instanceof HTMLElement) {
    scrollToGroupHeader(header);
  }
}

function initGroupAlertColumnHighlight(container) {
  const alertElements = container.querySelectorAll("[data-group-alerts], [data-group-missing-teacher-alerts]");
  if (alertElements.length === 0) {
    return;
  }

  let selectedGroupId = "";

  const getAlertGroupId = (target) => {
    if (!(target instanceof Element)) {
      return "";
    }

    return String(target.closest("[data-group-alert-item]")?.getAttribute("data-group-alert-item") || "").trim();
  };

  alertElements.forEach((alertsElement) => alertsElement.addEventListener("click", (event) => {
    const groupId = getAlertGroupId(event.target);
    if (groupId === "") {
      return;
    }

    const link = event.target instanceof Element ? event.target.closest("a") : null;
    if (link instanceof HTMLAnchorElement) {
      event.preventDefault();
    }

    if (selectedGroupId !== "" && selectedGroupId !== groupId) {
      setGroupAlertColumnHighlight(container, selectedGroupId, false);
    }

    selectedGroupId = groupId;
    setGroupAlertColumnHighlight(container, groupId, true);
    scrollToGroupAlertColumn(container, groupId);
  }));
}

function applyResponseState(container, checkbox, payload) {
  const data = payload?.data ?? {};
  const counts = data.counts ?? {};

  Object.entries(counts).forEach(([changedGroupId, countPayload]) => {
    renderGroupCount(container, changedGroupId, countPayload);
  });

  const detachedGroup = data.detached_group ?? null;
  if (detachedGroup?.id) {
    const detachedCheckbox = container.querySelector(
      `.js-group-membership[data-student-id="${data.student_id}"][data-group-id="${detachedGroup.id}"]`,
    );
    if (detachedCheckbox && detachedCheckbox !== checkbox) {
      detachedCheckbox.checked = false;
      updateColumnToggleState(container, String(detachedGroup.id));
    }
  }

  updateColumnToggleState(container, checkbox.dataset.groupId);
}

async function sendAssignmentUpdate(container, checkbox) {
  const apiUrl = container.dataset.groupAssignmentsApi;
  const csrfToken = container.dataset.groupAssignmentsCsrf || "";
  const schoolClassId = Number.parseInt(container.dataset.groupAssignmentsSchoolClassId || "0", 10) || 0;

  return postJson(
    apiUrl,
    {
      group_id: Number.parseInt(checkbox.dataset.groupId || "0", 10) || 0,
      student_id: Number.parseInt(checkbox.dataset.studentId || "0", 10) || 0,
      school_class_id: schoolClassId,
      value: checkbox.checked,
    },
    csrfToken,
  );
}

async function handleSingleCheckboxChange(container, checkbox, options = {}) {
  const { silent = false } = options;
  const intendedValue = checkbox.checked;

  checkbox.disabled = true;

  try {
    const payload = await sendAssignmentUpdate(container, checkbox);
    applyResponseState(container, checkbox, payload);

    if (!silent && payload?.toast?.message) {
      showToast(payload.toast.message, { type: payload.toast.type || "success" });
    }

    return true;
  } catch (error) {
    checkbox.checked = !intendedValue;
    updateColumnToggleState(container, checkbox.dataset.groupId);

    if (!silent) {
      showToast(String(error?.message || "Nie udało się zapisać przydziału."), { type: "danger" });
    }

    return false;
  } finally {
    checkbox.disabled = false;
  }
}

async function handleColumnToggle(container, toggle) {
  const groupId = toggle.dataset.groupId || "0";
  const targetValue = toggle.checked;
  const checkboxes = getVisibleGroupCheckboxes(container, groupId);
  const changedCheckboxes = checkboxes.filter((checkbox) => checkbox.checked !== targetValue);

  if (changedCheckboxes.length === 0) {
    updateColumnToggleState(container, groupId);
    return;
  }

  toggle.disabled = true;

  let successCount = 0;
  let errorCount = 0;

  for (const checkbox of changedCheckboxes) {
    checkbox.checked = targetValue;
    const ok = await handleSingleCheckboxChange(container, checkbox, { silent: true });
    if (ok) {
      successCount += 1;
    } else {
      errorCount += 1;
    }
  }

  toggle.disabled = false;
  updateColumnToggleState(container, groupId);

  if (errorCount === 0) {
    showToast(`Zapisano ${successCount} zmian w grupie.`, { type: "success" });
    return;
  }

  showToast(`Zapisano ${successCount} zmian, ${errorCount} nie udało się wykonać.`, { type: "warning" });
}

export function initGroupContextFilters(root = document) {
  const scope = root instanceof Element ? root : document;
  const selects = Array.from(scope.querySelectorAll("select[data-group-context-select]"));

  selects.forEach((schoolClassSelect) => {
    if (!(schoolClassSelect instanceof HTMLSelectElement)) {
      return;
    }

    const form = schoolClassSelect.closest("form");
    if (!(form instanceof HTMLFormElement) || form.dataset.groupContextFilterInit === "1") {
      return;
    }

    form.dataset.groupContextFilterInit = "1";

    const syncFormContext = () => {
      const selectedOption = schoolClassSelect.options[schoolClassSelect.selectedIndex] ?? null;
      const route = String(selectedOption?.dataset?.route ?? "").trim();
      if (route !== "") {
        form.action = route;
      }

      const filteredSchoolClassSelect = form.querySelector("[data-filter-school-class-select]");
      if (filteredSchoolClassSelect instanceof HTMLSelectElement) {
        filteredSchoolClassSelect.disabled = schoolClassSelect.value !== "0";
      }
    };

    form.addEventListener("submit", syncFormContext);
    form.addEventListener("form:before-serialize", syncFormContext);
  });

  return selects.length > 0;
}

export function initGroupAssignments(root = document) {
  const container = getContainer(root);
  if (!container || container.dataset.groupAssignmentsInitialized === "1") {
    return;
  }

  container.dataset.groupAssignmentsInitialized = "1";
  const warningKey = `group-workload-warning:${window.location.pathname}`;
  const workloadWarning = window.sessionStorage.getItem(warningKey);
  if (workloadWarning) {
    window.sessionStorage.removeItem(warningKey);
    showToast(workloadWarning, { type: "warning", autohide: false });
  }
  initGroupStudentSearch(container);
  initEditorSelects(container);
  initGroupAlertColumnHighlight(container);

  const modalEl = getEditorModalElement(container);
  const splitModalEl = getSplitModalElement(container);
  const studentsModalEl = getStudentsModalElement(container);

  container.querySelectorAll(".js-group-column-toggle").forEach((toggle) => {
    updateColumnToggleState(container, toggle.dataset.groupId || "0");
  });

  if (modalEl) {
    modalEl.addEventListener("shown.bs.modal", () => {
      const firstField = container.querySelector("#group-create-name");
      if (firstField instanceof HTMLElement) {
        firstField.focus();
      }
    });

    modalEl.addEventListener("hidden.bs.modal", () => {
      const form = getEditorForm(container);
      if (form) {
        form.reset();
        setCreateSubmitting(form, false);
      }
      void setEditorMode(container, "create");
    });
  }

  if (splitModalEl) {
    splitModalEl.addEventListener("shown.bs.modal", () => {
      const firstField = container.querySelector("#group-split-count");
      if (firstField instanceof HTMLElement) {
        firstField.focus();
        if ("select" in firstField) {
          firstField.select();
        }
      }
    });

    splitModalEl.addEventListener("hidden.bs.modal", () => {
      const form = getSplitForm(container);
      if (form) {
        form.reset();
        setSplitSubmitting(form, false);
      }
      setSplitMode(container);
    });
  }

  if (studentsModalEl) {
    studentsModalEl.addEventListener("shown.bs.modal", () => {
      const firstField = container.querySelector("#group-students-source-group");
      if (firstField instanceof HTMLElement) {
        firstField.focus();
      }
    });

    studentsModalEl.addEventListener("hidden.bs.modal", () => {
      const form = getStudentsForm(container);
      if (form) {
        form.reset();
        setStudentsSubmitting(form, false);
      }
      setStudentsMode(container);
    });
  }

  void openRequestedEditor(container);

  container.addEventListener("change", async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (target.matches(".js-group-membership")) {
      await handleSingleCheckboxChange(container, target);
      return;
    }

    if (target.matches(".js-group-column-toggle")) {
      await handleColumnToggle(container, target);
    }
  });

  container.addEventListener("click", async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (target.closest(".js-group-create-toggle")) {
      await setEditorMode(container, "create");
      setCreatePanelState(container, true);
      return;
    }

    const editButton = target.closest(".js-group-edit");
    if (editButton instanceof HTMLButtonElement) {
      await setEditorMode(container, "edit", getEditorGroupFromButton(editButton));
      setCreatePanelState(container, true);
      return;
    }

    if (target.closest(".js-group-delete-inactive-toggle")) {
      const modal = container.querySelector(".js-group-delete-inactive-modal");
      if (modal) bootstrap.Modal.getOrCreateInstance(modal).show();
      return;
    }

    if (target.closest(".js-group-delete-inactive-confirm")) {
      void deleteInactiveGroups(container);
      return;
    }

    const deleteButton = target.closest(".js-group-delete");
    if (deleteButton instanceof HTMLButtonElement) {
      void deleteGroup(container, deleteButton);
      return;
    }

    const splitButton = target.closest(".js-group-split");
    if (splitButton instanceof HTMLButtonElement) {
      setSplitMode(container, {
        id: splitButton.dataset.groupId || "",
        name: splitButton.dataset.groupName || "",
      });
      setSplitPanelState(container, true);
      return;
    }

    const studentsButton = target.closest(".js-group-students-modal-toggle");
    if (studentsButton instanceof HTMLButtonElement) {
      setStudentsMode(container, {
        id: studentsButton.dataset.groupId || "",
        name: studentsButton.dataset.groupName || "",
        teacherLabel: studentsButton.dataset.groupTeacherLabel || "",
      });
      setStudentsPanelState(container, true);
      return;
    }

    if (target.closest(".js-group-create-cancel")) {
      const form = getEditorForm(container);
      if (form) {
        form.reset();
      }
      setCreatePanelState(container, false);
      return;
    }

    if (target.closest(".js-group-split-cancel")) {
      const form = getSplitForm(container);
      if (form) {
        form.reset();
      }
      setSplitPanelState(container, false);
      return;
    }

    if (target.closest(".js-group-students-cancel")) {
      const form = getStudentsForm(container);
      if (form) {
        form.reset();
      }
      setStudentsPanelState(container, false);
    }
  });

  container.addEventListener("submit", async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLFormElement)) {
      return;
    }

    if (target.matches(".js-group-create-form")) {
      event.preventDefault();
      await handleCreateFormSubmit(container, target);
      return;
    }

    if (target.matches(".js-group-split-form")) {
      event.preventDefault();
      await handleSplitFormSubmit(container, target);
      return;
    }

    if (target.matches(".js-group-students-form")) {
      event.preventDefault();
      await handleStudentsFormSubmit(container, target);
    }
  });
}
